/**
 * Lesion / intracranial volume math. Ported verbatim from
 * `/home/joshua/Documents/OpenADS/src/ads/reporting/radiology_auto.py`
 * (`voxel_volume_ml`, `compute_volumes_ml`, `estimate_icv_ml`).
 *
 * PRECONDITION (same as the rest of Phase 2's report stage): the lesion mask
 * and, if supplied, the ICV/brain mask are assumed already registered to
 * MNI152 space, RAS-oriented (this app's `loadNiftiFile`/`reorientVolumeToRAS`
 * already guarantee RAS on load — see nifti.ts). Registration itself is a
 * separate, not-yet-wired workstream; this module does not register anything.
 */
import type { Dims } from "../volume";
import { voxelCount } from "../volume";

export function voxelVolumeMl(pixdim: Dims): number {
  // mm^3 -> ml
  return (pixdim[0] * pixdim[1] * pixdim[2]) / 1000;
}

export interface LesionVolumes {
  totalMl: number;
  leftMl: number;
  rightMl: number;
  /** Binarized (>0.5) lesion mask, same voxel order as the input. */
  binaryMask: Uint8Array;
}

/**
 * `compute_volumes_ml`: total + left/right split. The Python source splits
 * on `lesion_bin.shape[0] // 2` in RAS array space — x is the first (fastest
 * F-order) axis, so `[:x_mid]` is the low-R half. Faithfully reproduced here
 * on an F-order Float32Array (see volume.ts's fToC/cToF convention used
 * throughout this app).
 */
export function computeLesionVolumesMl(lesionProb: Float32Array, dims: Dims, pixdim: Dims): LesionVolumes {
  const vv = voxelVolumeMl(pixdim);
  const n = voxelCount(dims);
  const bin = new Uint8Array(n);
  for (let i = 0; i < n; i++) bin[i] = lesionProb[i] > 0.5 ? 1 : 0;

  const [nx, ny, nz] = dims;
  const xMid = nx >> 1;
  let leftVox = 0;
  let rightVox = 0;
  let totalVox = 0;
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      const rowBase = y * nx + z * nx * ny;
      for (let x = 0; x < nx; x++) {
        const v = bin[x + rowBase];
        if (!v) continue;
        totalVox++;
        if (x < xMid) leftVox++;
        else rightVox++;
      }
    }
  }
  return {
    totalMl: totalVox * vv,
    leftMl: leftVox * vv,
    rightMl: rightVox * vv,
    binaryMask: bin,
  };
}

/** `estimate_icv_ml`: sum of a binarized brain mask; 1200 mL fallback matches the Python default. */
export function estimateIcvMl(brainMask: Uint8Array | null, dims: Dims, pixdim: Dims): number {
  if (!brainMask) return 1200;
  const vv = voxelVolumeMl(pixdim);
  let vox = 0;
  const n = voxelCount(dims);
  for (let i = 0; i < n; i++) if (brainMask[i] > 0.5) vox++;
  return vox * vv;
}
